import { lazy } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { useTranslation } from 'react-i18next';
import {
  Activity,
  Gauge,
  Layers,
  Megaphone,
  Package,
  Server,
  SlidersHorizontal,
  Store,
} from 'lucide-react';
import { AccountPage, AppShell, LoginPage, RequireAuth, type NavItem } from '@iptv/ui';

const OverviewPage = lazy(() =>
  import('./pages/OverviewPage.js').then((m) => ({ default: m.OverviewPage })),
);
const TenantsPage = lazy(() =>
  import('./pages/TenantsPage.js').then((m) => ({ default: m.TenantsPage })),
);
const TenantDetailPage = lazy(() =>
  import('./pages/TenantDetailPage.js').then((m) => ({ default: m.TenantDetailPage })),
);
const HostLibraryPage = lazy(() =>
  import('./pages/HostLibraryPage.js').then((m) => ({ default: m.HostLibraryPage })),
);
const PlansPage = lazy(() =>
  import('./pages/PlansPage.js').then((m) => ({ default: m.PlansPage })),
);
const ReleasesPage = lazy(() =>
  import('./pages/ReleasesPage.js').then((m) => ({ default: m.ReleasesPage })),
);
const GlobalSettingsPage = lazy(() =>
  import('./pages/GlobalSettingsPage.js').then((m) => ({ default: m.GlobalSettingsPage })),
);
const AnnouncementsPage = lazy(() =>
  import('./pages/AnnouncementsPage.js').then((m) => ({ default: m.AnnouncementsPage })),
);
const AdminAuditPage = lazy(() =>
  import('./pages/AdminAuditPage.js').then((m) => ({ default: m.AdminAuditPage })),
);

function Shell() {
  const { t } = useTranslation();
  const nav: NavItem[] = [
    {
      to: '/',
      label: t('nav.dashboard'),
      shortLabel: t('navShort.dashboard'),
      icon: Gauge,
      end: true,
    },
    { to: '/tenants', label: t('nav.tenants'), shortLabel: t('navShort.tenants'), icon: Store },
    {
      to: '/hosts',
      label: t('nav.hostLibrary'),
      shortLabel: t('navShort.hostLibrary'),
      icon: Server,
    },
    { to: '/plans', label: t('nav.plans'), shortLabel: t('navShort.plans'), icon: Layers },
    {
      to: '/releases',
      label: t('nav.releases'),
      shortLabel: t('navShort.releases'),
      icon: Package,
    },
    {
      to: '/announcements',
      label: t('nav.announcements'),
      shortLabel: t('navShort.announcements'),
      icon: Megaphone,
    },
    {
      to: '/settings',
      label: t('nav.globalSettings'),
      shortLabel: t('navShort.globalSettings'),
      icon: SlidersHorizontal,
    },
    { to: '/audit', label: t('nav.audit'), shortLabel: t('navShort.audit'), icon: Activity },
  ];
  return (
    <RequireAuth>
      <AppShell title={t('common.appTitleAdmin')} nav={nav} />
    </RequireAuth>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route element={<Shell />}>
          <Route index element={<OverviewPage />} />
          <Route path="tenants" element={<TenantsPage />} />
          <Route path="tenants/:id" element={<TenantDetailPage />} />
          <Route path="hosts" element={<HostLibraryPage />} />
          <Route path="plans" element={<PlansPage />} />
          <Route path="releases" element={<ReleasesPage />} />
          <Route path="announcements" element={<AnnouncementsPage />} />
          <Route path="settings" element={<GlobalSettingsPage />} />
          <Route path="audit" element={<AdminAuditPage />} />
          <Route path="account" element={<AccountPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}
